import type { FormEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ClinicFallbackMark, PortalIcon } from '@/components/portal/PortalIcon';
import { PortalAuthFooter } from '@/components/portal/PortalChrome';
import { UI_LANG_STORAGE_KEY, uiLocale } from '@/lib/i18n';

export function PortalAuthShell({
  clinicName,
  logoUrl,
  title,
  subtitle,
  children,
  under,
  bookHref,
  bookLabel,
  lang,
}: {
  clinicName: string;
  logoUrl?: string | null;
  title?: string;
  subtitle?: string;
  children: ReactNode;
  under?: ReactNode;
  bookHref?: string | null;
  bookLabel?: string;
  lang?: 'da' | 'en';
}) {
  const copy = portalLoginCopy(lang ?? uiLocale());
  return (
    <div className="pt">
      <div className="auth">
        <main className="auth-main">
          <div className="auth-col">
            <div className="auth-brand">
              {logoUrl ? <img className="cl-logo" src={logoUrl} alt="" /> : <ClinicFallbackMark />}
              <div className="nm">{clinicName}</div>
              {title ? <h1>{title}</h1> : null}
              {subtitle ? <p>{subtitle}</p> : null}
            </div>
            {children}
            <div className="auth-under">
              {under}
              {bookHref ? (
                <Link to={bookHref} className="link" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <PortalIcon name="calendar" size="sm" />
                  {bookLabel ?? copy.book}
                </Link>
              ) : null}
            </div>
          </div>
        </main>
        <PortalAuthFooter poweredLabel={copy.powered} />
      </div>
    </div>
  );
}

export function PortalLangSwitch({
  language,
  onChange,
}: {
  language: 'da' | 'en';
  onChange: (lang: 'da' | 'en') => void;
}) {
  const copy = portalLoginCopy(language);
  return (
    <nav className="lang" aria-label={copy.language}>
      <button type="button" aria-current={language === 'da' ? true : undefined} lang="da" onClick={() => onChange('da')}>
        Dansk
      </button>
      <button type="button" aria-current={language === 'en' ? true : undefined} lang="en" onClick={() => onChange('en')}>
        English
      </button>
    </nav>
  );
}

export function portalLoginCopy(lang: 'da' | 'en') {
  if (lang === 'en') {
    return {
      title: 'Sign in to your patient portal',
      subtitle: 'See your appointments, message the clinic and fill in questionnaires.',
      email: 'Email',
      password: 'Password',
      showPassword: 'Show password',
      hidePassword: 'Hide password',
      forgot: 'Forgot your password?',
      submit: 'Sign in',
      submitting: 'Signing in…',
      noAccount: "Don't have an account? The clinic emails you an invitation once you are a patient here.",
      book: 'Book an appointment without signing in',
      powered: 'Powered by',
      language: 'Language',
      forgotTitle: 'Reset your password',
      forgotSubtitle: 'Enter the email you use for the portal. We send a link so you can choose a new password.',
      sendLink: 'Send reset link',
      sending: 'Sending…',
      sentTitle: 'Check your email',
      sentBody: (email: string) =>
        `If ${email} has an account, we have sent a link to reset the password. Check spam too.`,
      back: 'Back to sign in',
      resend: 'Send the link again',
      noAccessTitle: 'You do not have portal access yet',
      noAccessBody: (email: string) =>
        `You are signed in as ${email}, but we cannot find a patient portal for that email.`,
      noAccessReasonsTitle: 'This is usually one of two things',
      noAccessReason1: 'You have not opened the invitation from the clinic yet.',
      noAccessReason2: 'The clinic has a different email for you. Sign out and try that one.',
      signOutOther: 'Sign out and try another email',
      helpInvite: 'Need help? Write to the clinic from the portal after you have been invited.',
    };
  }
  return {
    title: 'Log ind på din patientportal',
    subtitle: 'Se dine aftaler, skriv til klinikken og udfyld spørgeskemaer.',
    email: 'E-mail',
    password: 'Adgangskode',
    showPassword: 'Vis adgangskode',
    hidePassword: 'Skjul adgangskode',
    forgot: 'Glemt adgangskode?',
    submit: 'Log ind',
    submitting: 'Logger ind…',
    noAccount: 'Har du ikke en konto? Klinikken sender dig en invitation på e-mail, når du er patient her.',
    book: 'Book en tid uden at logge ind',
    powered: 'Drevet af',
    language: 'Sprog',
    forgotTitle: 'Nulstil adgangskode',
    forgotSubtitle:
      'Skriv den e-mail, du bruger til portalen. Vi sender et link, så du kan vælge en ny adgangskode.',
    sendLink: 'Send link til nulstilling',
    sending: 'Sender…',
    sentTitle: 'Tjek din e-mail',
    sentBody: (email: string) =>
      `Hvis ${email} har en konto, har vi sendt et link til at nulstille adgangskoden. Kig også i spam.`,
    back: 'Tilbage til login',
    resend: 'Send linket igen',
    noAccessTitle: 'Du har ikke adgang til portalen endnu',
    noAccessBody: (email: string) =>
      `Du er logget ind som ${email}, men vi kan ikke finde en patientportal til den e-mail.`,
    noAccessReasonsTitle: 'Det skyldes typisk én af to ting',
    noAccessReason1: 'Du har ikke åbnet invitationen fra klinikken endnu.',
    noAccessReason2: 'Klinikken har en anden e-mail på dig. Log ud og prøv med den.',
    signOutOther: 'Log ud og prøv en anden e-mail',
    helpInvite: 'Brug for hjælp? Skriv til klinikken, når du har fået adgang.',
  };
}

export function resolvePortalGuestBookHref(input: {
  search: string;
  fromPath?: string | null;
  clinicSlug?: string | null;
}): string | null {
  const raw = input.search.startsWith('?') ? input.search.slice(1) : input.search;
  const params = new URLSearchParams(raw);
  const from = input.fromPath ?? params.get('from') ?? '';
  const fromSlug = from.match(/^\/book\/([^/?#]+)/)?.[1] ?? null;
  const slug = input.clinicSlug ?? params.get('clinic_slug') ?? fromSlug;
  return slug ? `/book/${slug}` : null;
}

function explicitPortalLanguage(value: string | null | undefined): 'da' | 'en' | null {
  if (typeof value !== 'string') return null;
  const tag = value.trim().toLowerCase();
  if (tag.startsWith('en')) return 'en';
  if (tag.startsWith('da')) return 'da';
  return null;
}

function readStoredPortalLoginLanguage(): string | null {
  try {
    return localStorage.getItem(UI_LANG_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Saved choice in `clinio-v2-ui-lang`, then the app i18n language.
 * Otherwise Danish. Browser language is ignored.
 */
export function portalLoginLanguageFromSaved(input: {
  stored?: string | null;
  i18nLanguage?: string | null;
}): 'da' | 'en' {
  return explicitPortalLanguage(input.stored) ?? explicitPortalLanguage(input.i18nLanguage) ?? 'da';
}

/** Language for a portal login visit: saved choice, otherwise Danish. */
export function initialPortalLoginLanguage(i18nLanguage?: string | null): 'da' | 'en' {
  return portalLoginLanguageFromSaved({
    stored: readStoredPortalLoginLanguage(),
    i18nLanguage,
  });
}

/**
 * Persist (`clinio-v2-ui-lang`) only when the patient taps Dansk or English.
 * A visit with no tap uses the saved choice, or Danish, and does not write.
 */
export function applyPortalLoginLanguage(input: {
  stored?: string | null;
  i18nLanguage?: string | null;
  picked?: 'da' | 'en' | null;
  persist: (lang: 'da' | 'en') => void;
}): 'da' | 'en' {
  if (input.picked) {
    input.persist(input.picked);
    return input.picked;
  }
  return portalLoginLanguageFromSaved(input);
}

export function handlePortalLangClick(
  event: FormEvent,
  onChange: (lang: 'da' | 'en') => void,
  lang: 'da' | 'en',
) {
  event.preventDefault();
  onChange(lang);
}
